/**
 * Pure field-validation logic for public form answers (Issue #439, F08).
 *
 * Extracted from PublicForm.jsx so the validation rules — especially the
 * repeatable-field path that had a temporal-dead-zone bug (F01) — are unit-
 * testable without rendering the full React component.
 *
 * The function is pure: it takes the flat field list, the current answers
 * dict, and a fieldsById map (for show_when cascade evaluation), and
 * returns an errors dict keyed by field_id (or field_id__index for
 * repeatable fields).
 *
 * "Required" is enforced separately from format validation: a blank
 * required field gets 'Required', a filled field with a bad format gets
 * the format error string. A blank optional field gets no error.
 */
import { STRUCTURAL_TYPES } from './formConstants';
import { evaluateShowWhen } from './fieldConditions';
import { validateFieldByType } from './fieldValidators';

/**
 * Validate form answers against field definitions.
 *
 * @param {Array} flatFields - already-flattened field list (use flattenFields())
 * @param {Object} answers - current answer dict keyed by field_id
 * @param {Object} fieldsById - field lookup map for show_when evaluation
 * @returns {Object} errors dict keyed by field_id or `${field_id}__${i}`
 */
export function validateFormAnswers(flatFields, answers, fieldsById) {
    const errors = {};
    for (const f of flatFields) {
        if (STRUCTURAL_TYPES.includes(f.field_type)) continue;
        if (f.hidden) continue;  // never rendered → can never be filled → never required
        if (!evaluateShowWhen(f, answers, fieldsById)) continue;

        if (f.repeatable) {
            let i = 0;
            while (answers[`${f.field_id}__${i}`] !== undefined || i < (f.repeatable_min || 1)) {
                const val = answers[`${f.field_id}__${i}`];
                if (f.required) {
                    if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
                        errors[`${f.field_id}__${i}`] = 'Required';
                    }
                }
                if (val) {
                    const fmtErr = validateFieldByType(f.field_type, val);
                    if (fmtErr) errors[`${f.field_id}__${i}`] = fmtErr;
                }
                i++;
                if (f.repeatable_max && i >= f.repeatable_max) break;
            }
            continue;
        }

        if (f.required) {
            const val = answers[f.field_id];
            if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
                errors[f.field_id] = 'Required';
            }
        }

        if (f.field_type === 'multi_select' && f.config?.max_selections) {
            const maxSel = f.config.max_selections;
            const val = answers[f.field_id];
            if (Array.isArray(val) && val.length > maxSel) {
                errors[f.field_id] = `Select at most ${maxSel} selection${maxSel === 1 ? '' : 's'}`;
            }
        }
        
        if (answers[f.field_id]) {
            const fmtErr = validateFieldByType(f.field_type, answers[f.field_id]);
            if (fmtErr) errors[f.field_id] = fmtErr;
        }
    }
    return errors;
}
